// TourContext — the first-run product tour state machine.
//
// Three states, nothing more: 'idle' (nothing on screen), 'prompt' (the
// take-a-tour ask) and 'running' (step N of the overlay). Everything visual
// lives in components/tour/; this file owns WHEN, WHERE and WHETHER-AGAIN.
//
// Completion is remembered per user (`pse_tour_v1:<userId>`), not per machine —
// a shared classroom laptop gets a fresh ask for the next teacher. Machine
// scope stays as the fallback for the dev-local user with no id.
//
// Navigation rides the `navigate-to-*` window events App.jsx already listens
// for, so this provider sits happily ABOVE AppContent and never needs its page
// setter.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { TOUR_STEPS, TOUR_STORAGE_KEY, PAGE_PATHS } from '../components/tour/tourSteps';
import { runTourAction } from '../components/tour/tourRuntime';

const TourContext = createContext(null);

function storageKey(userId) {
  return userId ? `${TOUR_STORAGE_KEY}:${userId}` : TOUR_STORAGE_KEY;
}

function readSeen(userId) {
  try {
    // Machine-wide value honoured too: it's what pre-per-user installs wrote.
    return localStorage.getItem(storageKey(userId)) || localStorage.getItem(TOUR_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeSeen(userId, value) {
  try {
    localStorage.setItem(storageKey(userId), value);
  } catch {
    // storage unavailable — the tour just asks again next launch
  }
}

export function TourProvider({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  const userId = user?.userId || null;

  const [status, setStatus] = useState('idle'); // 'idle' | 'prompt' | 'running'
  const [index, setIndex] = useState(0);
  // One ask per app run, whatever the auth state does afterwards.
  const askedThisRun = useRef(false);

  const step = status === 'running' ? TOUR_STEPS[index] || null : null;

  // Navigate to the page a step lives on. Leaving the design editor is the one
  // guarded exit: its unsaved-work modal would open UNDERNEATH this overlay and
  // deadlock both, so a dirty canvas ends the tour and hands the user back to
  // the guard. The tour never edits anything, so this can only fire on a draft
  // the editor restored before the tour reached it.
  // Brightivity: steps navigate with the router (desktop fired navigate-to-* window events).
  const routerNavigate = useNavigate();
  const navigateTo = useCallback((page) => {
    if (!page) return true;
    const path = PAGE_PATHS[page];
    if (!path) { console.warn('[tour] no route for page:', page); return true; }
    if (window.location.pathname !== path) routerNavigate(path);
    return true;
  }, [routerNavigate]);

  const finish = useCallback((outcome) => {
    // Leaving mid-step still owes the step's exit action, or the tour strands
    // e.g. the account modal open over the app after a Skip tour.
    if (status === 'running') runTourAction(TOUR_STEPS[index]?.after);
    setStatus('idle');
    setIndex(0);
    writeSeen(userId, outcome);
    delete window.__pseTourActive;
    document.body.classList.remove('pse-tour-active');
  }, [userId, status, index]);

  const start = useCallback(() => {
    window.__pseTourActive = true;
    document.body.classList.add('pse-tour-active');
    setIndex(0);
    setStatus('running');
  }, []);

  const decline = useCallback(() => {
    finish('declined');
  }, [finish]);

  const skip = useCallback(() => {
    finish('skipped');
  }, [finish]);

  const complete = useCallback(() => {
    finish('done');
  }, [finish]);

  const goTo = useCallback((nextIndex) => {
    const current = TOUR_STEPS[index];
    const target = TOUR_STEPS[nextIndex];
    if (!target) {
      complete();
      return;
    }
    const leavingDesign = current?.page === 'design' && target.page !== 'design';
    if (leavingDesign && window.__pseUnsavedGuard?.isDirty?.()) {
      // End first, THEN navigate — the guard modal needs a clickable screen.
      finish('interrupted');
      setTimeout(() => navigateTo(target.page), 0);
      return;
    }
    // Exit action before entry action (the step effect fires `before`), so the
    // account modal closes before the next step starts hunting for its anchor.
    if (current?.after && current.after !== target.before) runTourAction(current.after);
    setIndex(nextIndex);
  }, [index, complete, finish, navigateTo]);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const back = useCallback(() => goTo(Math.max(0, index - 1)), [goTo, index]);

  // Drive the page for the active step. Kept here rather than in the overlay so
  // the overlay stays a pure renderer of "current step + measured rect".
  useEffect(() => {
    if (status !== 'running' || !step) return;
    navigateTo(step.page);
    runTourAction(step.before);
    if (step.page === 'browser') {
      // Ask PrintBrowser to show its site picker instead of restoring the last
      // site — see the BrowserView note in tourSteps.js.
      window.dispatchEvent(new Event('pse-tour-active'));
    }
  }, [status, step, navigateTo]);

  // First-run ask: only once the user is in and every auth/onboarding modal is
  // out of the way. `deferPrompt` is what App.jsx uses to say "onboarding is
  // still on screen" — the tour is the last thing to speak on a first launch.
  const [deferPrompt, setDeferPrompt] = useState(true);

  useEffect(() => {
    if (loading || !isAuthenticated || deferPrompt) return;
    if (askedThisRun.current || status !== 'idle') return;
    if (readSeen(userId)) return;
    askedThisRun.current = true;
    setStatus('prompt');
  }, [loading, isAuthenticated, deferPrompt, status, userId]);

  // Replay hook — the settings gear, and anything else that wants it.
  useEffect(() => {
    const onReplay = () => {
      askedThisRun.current = true;
      start();
    };
    window.addEventListener('pse-start-tour', onReplay);
    return () => window.removeEventListener('pse-start-tour', onReplay);
  }, [start]);

  useEffect(() => () => {
    delete window.__pseTourActive;
    document.body.classList.remove('pse-tour-active');
  }, []);

  const value = useMemo(() => ({
    status,
    step,
    index,
    total: TOUR_STEPS.length,
    isFirst: index === 0,
    isLast: index === TOUR_STEPS.length - 1,
    start,
    decline,
    skip,
    complete,
    next,
    back,
    setDeferPrompt,
  }), [status, step, index, start, decline, skip, complete, next, back]);

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside a TourProvider');
  return ctx;
}

export default TourContext;
